import i18next from 'i18next';
import { SagaIterator } from 'redux-saga';
import { all, call, takeEvery } from 'redux-saga/effects';
import {
  PapiDaSharedAncCreate,
  PapiDaSharedAncPublish,
  PapiDaSharedAncUpdate,
  PapiRequestDaSharedAncCreate,
  PapiRequestDaSharedAncUpdate,
} from '@wix/da-papi-types';
import { isApiError, requestNapi } from '@wix/da-http-client';
import { putErrorNotification } from '@wix/da-shared-react/pkg/utils/saga';
import { getContentFromStorage } from '@wix/da-editor-tiptap/pkg/components/utils/tiptap';
import { SchemaVersion } from '@wix/da-editor-tiptap/pkg/server/types';
import {
  createArtsAndCraftsDeviation,
  publishArtsAndCraftsDeviation,
  updateArtsAndCraftsDeviation,
} from '../actions/artsAndCrafts';
import {
  ArtsAndCraftsDeviationFieldName,
  ArtsAndCraftsDeviationFormFields,
} from '../../modals/ArtsAndCraftsDeviationPopup/types';

function* throwError(errorMessage: string, response?: any) {
  if (response && response.errorCode === 400 && response.errorDetails?.errorKey) {
    yield putErrorNotification(
      i18next.t(`deviationSubmit.validation.error.${response.errorDetails.errorKey}`)
    );
    return;
  }
  yield putErrorNotification(errorMessage);
}

function formDataToRequestData(
  formData: ArtsAndCraftsDeviationFormFields
): PapiRequestDaSharedAncCreate | PapiRequestDaSharedAncUpdate {
  const formDescription = formData[ArtsAndCraftsDeviationFieldName.DESCRIPTION];
  const formQuestions = formData[ArtsAndCraftsDeviationFieldName.QUESTIONS] ?? [];
  const questions = formQuestions.map(question => ({
    questionid: question.questionId,
    editorRaw: question.questionEditor
      ? question.questionEditor()
      : JSON.stringify({
          version: SchemaVersion.V1,
          document: getContentFromStorage(question.question),
        }),
    type: question.type,
    required: question.isRequired,
    answers:
      question.answers &&
      question.answers.map(answer => ({
        answerid: answer.answerId,
        label: answer.answer,
        price_modifier: answer.priceModifier || undefined,
      })),
  }));
  return {
    title: formData[ArtsAndCraftsDeviationFieldName.TITLE],
    is_mature: formData[ArtsAndCraftsDeviationFieldName.MATURE_CONTENT],
    category: formData[ArtsAndCraftsDeviationFieldName.CATEGORY],
    tags: formData[ArtsAndCraftsDeviationFieldName.TAGS],
    add_watermark: formData[ArtsAndCraftsDeviationFieldName.ADD_WATERMARK],
    display_resolution: Number(
      formData[ArtsAndCraftsDeviationFieldName.DISPLAY_RESOLUTION]
    ) as PapiRequestDaSharedAncCreate['display_resolution'],
    base_price: formData[ArtsAndCraftsDeviationFieldName.PRICE],
    shipping_profileid: formData[ArtsAndCraftsDeviationFieldName.SHIPPING_PROFILE],
    editorRaw: formDescription && formDescription(),
    questions,
  };
}

function* createArtsAndCraftsDeviationSaga(
  action: ReturnType<typeof createArtsAndCraftsDeviation>
): SagaIterator {
  const { formData, onSuccess, onError } = action.payload;
  const response: PapiDaSharedAncCreate = yield call(requestNapi, {
    method: 'post',
    url: '/anc/create',
    data: formDataToRequestData(formData),
  });

  if (!response || isApiError(response)) {
    yield call(throwError, i18next.t('deviationSubmit.error.writeDeviationServerError'), response);
    if (onError) {
      yield call(onError, response);
    }
    return;
  }

  if (onSuccess) {
    yield call(onSuccess, response.deviation);
  }
}

function* updateArtsAndCraftsDeviationSaga(
  action: ReturnType<typeof updateArtsAndCraftsDeviation>
): SagaIterator {
  const { deviationId, formData, onSuccess, onError } = action.payload;
  const response: PapiDaSharedAncUpdate = yield call(requestNapi, {
    method: 'post',
    url: '/anc/update',
    data: {
      deviationid: deviationId,
      is_open: formData[ArtsAndCraftsDeviationFieldName.OPEN_FOR_ORDERS],
      ...formDataToRequestData(formData),
    },
  });

  if (!response || isApiError(response)) {
    yield call(throwError, i18next.t('deviationSubmit.error.writeDeviationServerError'), response);
    if (onError) {
      yield call(onError, response);
    }
    return;
  }

  if (onSuccess) {
    yield call(onSuccess, response.deviation);
  }
}

function* publishArtsAndCraftsDeviationSaga(
  action: ReturnType<typeof publishArtsAndCraftsDeviation>
): SagaIterator {
  const { deviationId, onSuccess, onError } = action.payload;
  const response: PapiDaSharedAncPublish = yield call(requestNapi, {
    method: 'post',
    url: '/anc/publish',
    data: {
      deviationid: deviationId,
    },
  });

  if (!response || isApiError(response)) {
    yield call(
      throwError,
      i18next.t('deviationSubmit.error.publishDeviationServerError'),
      response
    );
    if (onError) {
      yield call(onError, response);
    }
    return;
  }

  if (onSuccess) {
    yield call(onSuccess);
  }
}

export default function* artsAndCraftsSaga() {
  yield all([
    takeEvery(createArtsAndCraftsDeviation, createArtsAndCraftsDeviationSaga),
    takeEvery(updateArtsAndCraftsDeviation, updateArtsAndCraftsDeviationSaga),
    takeEvery(publishArtsAndCraftsDeviation, publishArtsAndCraftsDeviationSaga),
  ]);
}
